/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import Link from "next/link";
import { useRef, useEffect, useState } from "react";

type SectionType = "hero" | "text" | "text-left" | "list" | "iframe";
interface Section {
  type: SectionType;
  data: any;
}

export default function SectionRenderer({ section }: { section: Section }) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [isScrollLocked, setIsScrollLocked] = useState(false);

  // Lock page scroll when button is clicked so wheel/gesture events don't scroll the parent page.
  useEffect(() => {
    if (section.type !== "iframe") return;

    let scrollPosRef = 0;

    const lockScroll = () => {
      if (document.body.dataset.scrollLocked === "true") return;
      scrollPosRef = window.scrollY;
      // Apply inline styles to avoid needing a global css class.
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollPosRef}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
      document.body.dataset.scrollLocked = "true";
    };

    const unlockScroll = () => {
      if (document.body.dataset.scrollLocked !== "true") return;
      const y = scrollPosRef;
      // Clear styles
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      delete document.body.dataset.scrollLocked;
      window.scrollTo(0, y);
    };

    // Lock or unlock scroll based on state
    if (isScrollLocked) {
      lockScroll();
    } else {
      unlockScroll();
    }

    // Also unlock on window blur just in case.
    const handleWindowBlur = () => {
      unlockScroll();
      setIsScrollLocked(false);
    };

    window.addEventListener("blur", handleWindowBlur);

    // Defensive: if user presses Escape while locked, unlock.
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        unlockScroll();
        setIsScrollLocked(false);
      }
    };
    window.addEventListener("keydown", handleKey);

    return () => {
      unlockScroll();
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("keydown", handleKey);
    };
  }, [section.type, isScrollLocked]);

  if (section.type === "hero") {
    const d = section.data || {};
    const alignMap: Record<string, string> = {
      left: "start",
      center: "center",
      right: "end",
    };
    const justify = alignMap[d.align] || "center";
    return (
      <div
        id={d.id || undefined}
        className="text-white d-flex align-items-center"
        style={{
          width: "100vw",
          height: "100vh",
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
          backgroundImage: d.backgroundImageUrl
            ? `url(${d.backgroundImageUrl})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",

          position: "relative",
        }}
      >
        <div
          className="w-100"
          style={{
            background: "rgba(0,0,0,0.35)",
            backdropFilter: "blur(1px)",
            height: "100%",
          }}
        >
          <div
            className={`container h-100 pb-5 d-flex flex-column justify-content-between align-items-${justify}`}
          >
            <div
              className={`d-flex flex-column py-5 my-5 justify-content-between align-items-${justify}`}
            >
              {d.title && <h1 className="display-5 fw-bold">{d.title}</h1>}
              {d.subtitle && <p className="lead">{d.subtitle}</p>}
            </div>
            {d.ctaLabel && d.ctaUrl && (
              <Link
                className="btn btn-primary my-5 p-3"
                style={{ maxWidth: "50vw" }}
                href={d.ctaUrl}
              >
                {d.ctaLabel}
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (section.type === "text-left") {
    const d = section.data || {};
    return (
      <div
        id={d.id || undefined}
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className=" mb-3 pe-3">
          <div className="card-body">
            {d.heading && <h2 className="h1 mb-3">{d.heading}</h2>}
            {d.body && (
              <div className="h5" style={{ whiteSpace: "pre-wrap" }}>
                {d.body}
              </div>
            )}
          </div>
        </div>
        <div
          className="flex-grow-1"
          style={{
            width: "100%",
            height: "50vh",
            background: `url(${d.imageUrl}) no-repeat center`,
            backgroundSize: "cover",
            borderRadius: 40,
          }}
        ></div>
      </div>
    );
  }

  if (section.type === "list") {
    const d = section.data || {};
    return (
      <div
        id={d.id || undefined}
        className="d-flex flex-column justify-content-center align-items-center my-5"
        style={{ minHeight: "50vh" }}
      >
        {d.heading && <h2 className="h1 mb-3">{d.heading}</h2>}
        {d.listItems && Array.isArray(d.listItems) && (
          <ul className="clean-feature-list mt-3">
            {d.listItems.map((item: string, idx: number) => (
              <li key={idx} className="clean-feature-item">
                <span className="clean-feature-bullet" aria-hidden="true" />
                <span className="clean-feature-text">{item}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (section.type === "iframe") {
    const d = section.data || {};
    return (
      <div
        id={d.id || undefined}
        className="d-flex flex-column align-items-center my-5"
      >
        <div className="d-flex flex-column justify-content-evenly w-100">
          {d.title && <h3 className="align-self-center">{d.title}</h3>}
          {d.lockableScroll && (
            <button
              onClick={() => setIsScrollLocked(!isScrollLocked)}
              className={`btn ${
                isScrollLocked ? "btn-success" : "btn-outline-secondary"
              } mb-3 align-self-end`}
              type="button"
              style={{ marginTop: -40 }}
            >
              {isScrollLocked ? "🔒 Scroll Locked" : "🔓 Lock Scroll"}
            </button>
          )}
        </div>
        <iframe
          src={
            d.src ||
            `${
              process.env.NEXT_PUBLIC_AUTOPILOT_ORIGIN ||
              "https://flightsim.jockan.com/"
            }/?iframe=true`
          }
          width={d.width || "1200"}
          height={d.height || "350"}
          title={d.title || "Embedded Content"}
          className="rounded-3"
          ref={iframeRef}
          // Prevent scroll chain just in case (modern browsers)
          style={{ overscrollBehavior: "contain" }}
        />
      </div>
    );
  }

  // Fallback unknown section
  return (
    <div className="alert alert-warning">
      Unknown section type: {section.type}
    </div>
  );
}
